import Permissions from "~/types/Permissions";
import Role from "~/types/Role";

export function canEditUserTarget(targetRoles: readonly { name: string }[] | undefined, editorRoles: readonly string[]): boolean {
  return !!targetRoles && targetRoles.every(role =>
    role.name === Role.SUPER_ADMIN
      ? editorRoles.includes(Role.SUPER_ADMIN)
      : role.name !== Role.ADMIN || editorRoles.includes(Role.SUPER_ADMIN) || editorRoles.includes(Role.ADMIN),
  );
}

/** Editing another user's credentials also requires creation permission. */
export function canEditUserCredentials(isOwnProfile: boolean, hasPermission: (permission: string | string[]) => boolean): boolean {
  return isOwnProfile || hasPermission(Permissions.UsersCreate);
}

/** Normal creation sends roles, so it also needs role assignment; locked-role creation (auditor) keeps create alone. */
export function canCreateUserWithRoles(lockRoles: boolean, hasPermission: (permission: string | string[]) => boolean): boolean {
  return hasPermission(Permissions.UsersCreate) && (lockRoles || hasPermission(Permissions.RolesAssign));
}

/** Disabling another user also requires delete permission; own status stays locked. */
export function canDisableUserTarget(isOwnProfile: boolean, hasPermission: (permission: string | string[]) => boolean): boolean {
  return !isOwnProfile && hasPermission(Permissions.UsersDelete);
}

/** Returns the authorized profile payload, or null when every requested change was filtered out. */
export function getUserUpdatePayload(values: Record<string, unknown>, original: Record<string, unknown>, canEditEmail: boolean, canDisableUser: boolean): Record<string, unknown> | null {
  const payload = Object.fromEntries(Object.entries(values).filter(([field, value]) =>
    (field !== "email" || canEditEmail)
    && (field !== "disabled" || (canDisableUser && value !== original.disabled)),
  ));
  return Object.entries(payload).some(([field, value]) => JSON.stringify(value) !== JSON.stringify(original[field]))
    ? payload
    : null;
}
